import { TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "./button";
import { cn } from "../utils";

/**
 * Failure to load. Distinct from `EmptyState` on purpose: "we could not fetch
 * this" and "there is nothing here" are different facts, and showing the second
 * when the first happened tells the user something untrue.
 */
export function ErrorState({
  title = "Não foi possível carregar",
  description = "Verifique sua conexão e tente de novo.",
  error,
  onRetry,
  retrying,
  action,
  className,
}: {
  title?: string;
  description?: string;
  error?: unknown;
  onRetry?: () => void;
  retrying?: boolean;
  action?: ReactNode;
  className?: string;
}) {
  const detail = formatUnknownError(error);
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center gap-2 px-6 py-12 text-center",
        className,
      )}
    >
      <div className="mb-1 flex size-10 items-center justify-center rounded-full border border-line bg-danger-subtle text-danger [&_svg]:size-5">
        <TriangleAlert />
      </div>
      <p className="text-sm font-medium text-ink">{title}</p>
      <p className="max-w-sm text-2xs text-ink-muted">{description}</p>
      {detail && (
        <p className="max-w-sm break-words font-mono text-2xs text-ink-faint">
          {detail}
        </p>
      )}
      {(action ?? onRetry) && (
        <div className="mt-2">
          {action ?? (
            <Button variant="secondary" size="sm" loading={retrying} onClick={onRetry}>
              Tentar de novo
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Eden Treaty throws the response body, not an Error. `String(that)` is
 * `[object Object]`. Walk the shapes we actually get before giving up —
 * a raw object on screen is worse than silence.
 */
export function formatUnknownError(error: unknown): string | null {
  if (error == null) return null;
  if (typeof error === "string") return usable(error);
  if (error instanceof Error) return usable(error.message);
  if (typeof error !== "object") return null;

  const record = error as Record<string, unknown>;
  const fromValue = messageFromValue(record.value);
  if (fromValue) return fromValue;
  const fromError = messageFromValue(record.error);
  if (fromError) return fromError;
  if (typeof record.message === "string") return usable(record.message);
  return null;
}

function messageFromValue(value: unknown): string | null {
  if (typeof value === "string") return usable(value);
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  if (typeof record.error === "string") return usable(record.error);
  if (typeof record.message === "string") return usable(record.message);
  return null;
}

function usable(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed || trimmed === "[object Object]") return null;
  return trimmed;
}
